import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useBrand } from "../../context/BrandContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const CheckingEmptyView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { brandConfig, isDarkMode } = useBrand();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);

  const handleCancel = () => {
    dispatch({ type: "SET_STEP", payload: 1 });
    navigate("/add-empty");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="73:922"
    >
      {/* Animated Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Spinning Ellipse with Gradient */}
        <motion.div
          className="absolute"
          style={{
            width: "1080px",
            height: "1080px",
            left: "50%",
            top: "50%",
            marginLeft: "-540px",
            marginTop: "-540px",
            borderRadius: "1000px",
            background:
              `linear-gradient(0deg, ${brandConfig.colors.primary[isDarkMode ? 'dark' : 'light']} 0%, rgba(255, 255, 255, 0.00) 25%, rgba(255, 255, 255, 0.00) 50%, rgba(255, 255, 255, 0.00) 75%, ${brandConfig.colors.primary[isDarkMode ? 'dark' : 'light']} 100%)`,
          }}
          animate={{
            rotate: [0, 360, 720, 1080], // Spin 3 full rotations
            scale: [1, 1.1, 1], // Scale up during spin, back to normal
          }}
          transition={{
            duration: 12, // Much slower for smooth, subtle motion
            ease: [0.25, 0.1, 0.25, 1], // Gentler easing curve
            repeat: Infinity,
            repeatDelay: 3, // Longer pause for more subtle effect
            times: [0, 0.25, 0.5, 0.75, 1], // Even distribution of keyframes
          }}
        />

        {/* Overlaid Rectangle with White Fill and Backdrop Blur */}
        <div
          className="absolute inset-0"
          style={{            
            backdropFilter: "blur(200px)",
            WebkitBackdropFilter: "blur(200px)",
          }}
        />
      </div>

      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Rows 1 & 2 - Main heading spanning two rows */}
          <div className="row-span-2 flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.6,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <Heading
                  title={getCopy('checkingEmpty.heading')}
                  subtitle={getCopy('checkingEmpty.subheading')}
                />
              </motion.div>
            </div>
          </div>

          {/* Row 3 - No email text */}
          <div className="flex items-end justify-center">
            <motion.div
              className="text-center w-full max-w-[680px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.3,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <div className="font-brand-secondary font-light leading-none not-italic text-[32px] text-center text-brand-text-primary">
                <MultiLineCopy 
                  text={getCopy('checkingEmpty.helpText')}
                  className="leading-[42px]"
                />
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Single Cancel Button */}
          <div className="flex justify-center">
            <Button variant="secondary" onClick={handleCancel}>
              {getCopy('checkingEmpty.buttons.secondary')}
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default CheckingEmptyView;
